import React, { useState, useEffect } from 'react';
import { Upload, Download, FileText, Calendar, TrendingUp, AlertCircle, CheckCircle, Clock, X, BarChart3, FileSpreadsheet, History, Settings } from 'lucide-react';
import api from '../../services/api';
import AccordionSection from '../../components/UI/AccordionSection';
import { InfoBadge } from '../../components/UI/InfoBadge';

const MOMWorkingDaysUpload = () => {
  const [uploadHistory, setUploadHistory] = useState([]);
  const [availableYears, setAvailableYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState('');
  const [yearSummary, setYearSummary] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [replaceExisting, setReplaceExisting] = useState(false);
  const [loading, setLoading] = useState(false);
  
  // Accordion states
  const [sectionsExpanded, setSectionsExpanded] = useState({
    upload: false,
    summary: true,
    history: false
  });

  useEffect(() => {
    fetchUploadHistory();
    fetchAvailableYears();
  }, []);

  useEffect(() => {
    if (selectedYear) {
      fetchYearSummary(selectedYear);
    }
  }, [selectedYear]);

  const fetchUploadHistory = async () => {
    try {
      const response = await api.get('/configuration/mom-working-days-uploads/');
      setUploadHistory(response.data.results || response.data);
    } catch (error) {
      console.error('Error fetching upload history:', error);
    }
  };

  const fetchAvailableYears = async () => {
    try {
      const response = await api.get('/configuration/mom-working-days-calendars/available_years/');
      setAvailableYears(response.data.years || []);
      if (response.data.years && response.data.years.length > 0) {
        setSelectedYear(response.data.years[response.data.years.length - 1]); // Select latest year
      }
    } catch (error) {
      console.error('Error fetching available years:', error);
    }
  };

  const fetchYearSummary = async (year) => {
    try {
      const response = await api.get(`/configuration/mom-working-days-calendars/year_summary/?year=${year}`);
      setYearSummary(response.data);
    } catch (error) {
      console.error('Error fetching year summary:', error);
      setYearSummary(null);
    }
  };

  const toggleSection = (section) => {
    setSectionsExpanded(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setSelectedFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileSelect = (e) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    setIsUploading(true);
    setUploadProgress({ status: 'uploading', message: 'Uploading file...' });

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('replace_existing', replaceExisting.toString());

              const response = await api.post('/configuration/mom-working-days-uploads/upload_file/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data.status === 'success') {
        setUploadProgress({
          status: 'success',
          message: `Successfully processed ${response.data.records_created} records for year ${response.data.year_processed}`
        });
        
        // Refresh data
        await fetchUploadHistory();
        await fetchAvailableYears();
        
        // Clear form
        setSelectedFile(null);
        setReplaceExisting(false);
        
        // Auto-expand history to show the new upload
        setSectionsExpanded(prev => ({ ...prev, history: true }));
      } else {
        setUploadProgress({
          status: 'error',
          message: response.data.message || 'Upload failed'
        });
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      setUploadProgress({
        status: 'error',
        message: error.response?.data?.message || 'Upload failed. Please try again.'
      });
    } finally {
      setIsUploading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'failed':
        return <AlertCircle className="w-4 h-4 text-red-500" />;
      case 'processing':
        return <Clock className="w-4 h-4 text-yellow-500 animate-spin" />;
      default:
        return <Clock className="w-4 h-4 text-gray-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'text-green-600';
      case 'failed':
        return 'text-red-600';
      case 'processing':
        return 'text-yellow-600';
      default:
        return 'text-gray-600';
    }
  };

  const downloadProcessingLog = async (uploadId) => {
    try {
              const response = await api.get(`/configuration/mom-working-days-uploads/${uploadId}/download_log/`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `upload_${uploadId}_log.txt`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading log:', error);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Calendar className="w-8 h-8 text-blue-600" />
          <div>
            <h2 className="text-xl font-semibold text-gray-900">MOM Working Days Calendar</h2>
            <p className="text-sm text-gray-600">Manage Singapore's official working days data</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500">Data for {availableYears.length} years</span>
        </div>
      </div>

      {/* Upload Section */}
      <AccordionSection
        title="Upload New File"
        icon={<Upload className="w-4 h-4 text-blue-600" />}
        iconBg="bg-blue-100"
        expanded={sectionsExpanded.upload}
        onToggle={() => toggleSection('upload')}
        theme="admin"
        contentVariant="padded"
        trailing={selectedFile ? <InfoBadge color="blue" label="File ready" /> : null}
      >
        {/* Compact File Upload Area */}
        <div
          className={`border-2 border-dashed rounded-xl p-4 text-center transition-colors ${
            dragActive
              ? 'border-blue-500 bg-blue-50'
              : 'border-gray-300 hover:border-gray-400'
          }`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
        >
          <FileSpreadsheet className="w-8 h-8 mx-auto text-gray-400 mb-2" />
          
          {selectedFile ? (
            <div className="space-y-2">
              <p className="text-sm font-medium text-gray-900">{selectedFile.name}</p>
              <p className="text-xs text-gray-500">
                {(selectedFile.size / 1024).toFixed(1)} KB
              </p>
              <button
                onClick={() => setSelectedFile(null)}
                className="text-sm text-red-600 hover:text-red-700 font-medium"
              >
                Remove file
              </button>
            </div>
          ) : (
            <div className="space-y-2">
              <p className="text-sm text-gray-600">
                Drop incomplete-month.xls file here or
              </p>
              <label className="inline-flex items-center px-4 py-2 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors cursor-pointer">
                <FileText className="w-4 h-4 mr-2" />
                Browse Files
                <input
                  type="file"
                  accept=".xls,.xlsx"
                  onChange={handleFileSelect}
                  className="hidden"
                />
              </label>
            </div>
          )}
        </div>

        {/* Upload Options */}
        {selectedFile && (
          <div className="mt-4 space-y-4">
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="replace-existing"
                checked={replaceExisting}
                onChange={(e) => setReplaceExisting(e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus-visible:ring-blue-500"
              />
              <label htmlFor="replace-existing" className="text-sm text-gray-700">
                Replace existing data for the same year
              </label>
            </div>

            <div className="flex justify-end">
              <button
                onClick={handleUpload}
                disabled={isUploading}
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isUploading ? (
                  <>
                    <Clock className="w-4 h-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Upload & Process
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {/* Upload Progress */}
        {uploadProgress && (
          <div className={`mt-4 p-3 rounded-lg ${
            uploadProgress.status === 'success' ? 'bg-green-50 border border-green-200' :
            uploadProgress.status === 'error' ? 'bg-red-50 border border-red-200' :
            'bg-yellow-50 border border-yellow-200'
          }`}>
            <div className="flex items-center gap-2">
              {uploadProgress.status === 'success' ? (
                <CheckCircle className="w-4 h-4 text-green-600" />
              ) : uploadProgress.status === 'error' ? (
                <AlertCircle className="w-4 h-4 text-red-600" />
              ) : (
                <Clock className="w-4 h-4 text-yellow-600" />
              )}
              <p className={`text-sm ${
                uploadProgress.status === 'success' ? 'text-green-700' :
                uploadProgress.status === 'error' ? 'text-red-700' :
                'text-yellow-700'
              }`}>
                {uploadProgress.message}
              </p>
            </div>
          </div>
        )}
      </AccordionSection>

      {/* Year Summary */}
      <AccordionSection
        title="Working Days Summary"
        icon={<BarChart3 className="w-4 h-4 text-green-600" />}
        iconBg="bg-green-100"
        expanded={sectionsExpanded.summary}
        onToggle={() => toggleSection('summary')}
        theme="admin"
        contentVariant="padded"
        trailing={yearSummary ? <InfoBadge color="green" label={`${yearSummary.year}`} /> : null}
      >
        <div className="space-y-4">
          {/* Compact Year Selection */}
          <div className="flex items-center gap-3">
            <label className="text-sm font-medium text-gray-700">Year:</label>
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(e.target.value)}
              className="px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
            >
              <option value="">Select Year</option>
              {availableYears.map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>

          {yearSummary && (
            <>
              {/* Year Totals */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-blue-50 p-4 rounded-xl border border-blue-200">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-blue-600" />
                    <div>
                      <div className="text-sm font-medium text-blue-900">5-Day Work Week</div>
                      <div className="text-2xl font-bold text-blue-600">{yearSummary.totals.five_day_week}</div>
                      <div className="text-xs text-blue-700">Total working days</div>
                    </div>
                  </div>
                </div>
                <div className="bg-green-50 p-4 rounded-xl border border-green-200">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-green-600" />
                    <div>
                      <div className="text-sm font-medium text-green-900">5.5-Day Work Week</div>
                      <div className="text-2xl font-bold text-green-600">{yearSummary.totals.five_half_day_week}</div>
                      <div className="text-xs text-green-700">Total working days</div>
                    </div>
                  </div>
                </div>
                <div className="bg-purple-50 p-4 rounded-xl border border-purple-200">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-purple-600" />
                    <div>
                      <div className="text-sm font-medium text-purple-900">6-Day Work Week</div>
                      <div className="text-2xl font-bold text-purple-600">{yearSummary.totals.six_day_week}</div>
                      <div className="text-xs text-purple-700">Total working days</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Monthly Breakdown */}
              <div className="md:hidden space-y-3 p-4">
                {yearSummary.months.map((month, index) => (
                  <div key={index} className="bg-white border border-gray-200 rounded-xl p-4">
                    <p className="text-sm font-medium text-gray-900">{month.month}</p>
                    <div className="mt-3 space-y-2">
                      <div className="flex items-start justify-between gap-3 text-sm">
                        <span className="text-gray-500">Calendar Days</span>
                        <span className="text-gray-900 tabular-nums">{month.calendar_days}</span>
                      </div>
                      <div className="flex items-start justify-between gap-3 text-sm">
                        <span className="text-gray-500">Sundays / Saturdays</span>
                        <span className="text-gray-900 tabular-nums">{month.sundays} / {month.saturdays}</span>
                      </div>
                      <div className="flex items-start justify-between gap-3 text-sm">
                        <span className="text-gray-500">5-Day</span>
                        <span className="text-blue-600 font-medium tabular-nums">{month.five_day_week}</span>
                      </div>
                      <div className="flex items-start justify-between gap-3 text-sm">
                        <span className="text-gray-500">5.5-Day</span>
                        <span className="text-green-600 font-medium tabular-nums">{month.five_half_day_week}</span>
                      </div>
                      <div className="flex items-start justify-between gap-3 text-sm">
                        <span className="text-gray-500">6-Day</span>
                        <span className="text-purple-600 font-medium tabular-nums">{month.six_day_week}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="table-header">
                    <tr className="border-b border-gray-200">
                      <th className="table-header-cell">Month</th>
                      <th className="table-header-cell text-right">Calendar Days</th>
                      <th className="table-header-cell text-right">Sundays</th>
                      <th className="table-header-cell text-right">Saturdays</th>
                      <th className="table-header-cell text-right text-blue-700">5-Day</th>
                      <th className="table-header-cell text-right text-green-700">5.5-Day</th>
                      <th className="table-header-cell text-right text-purple-700">6-Day</th>
                    </tr>
                  </thead>
                  <tbody>
                    {yearSummary.months.map((month, index) => (
                      <tr key={index} className="border-b border-gray-100 table-row">
                        <td className="py-2 px-3 font-medium text-gray-900">{month.month}</td>
                        <td className="py-2 px-3 text-right text-gray-600">{month.calendar_days}</td>
                        <td className="py-2 px-3 text-right text-gray-600">{month.sundays}</td>
                        <td className="py-2 px-3 text-right text-gray-600">{month.saturdays}</td>
                        <td className="py-2 px-3 text-right font-medium text-blue-600">{month.five_day_week}</td>
                        <td className="py-2 px-3 text-right font-medium text-green-600">{month.five_half_day_week}</td>
                        <td className="py-2 px-3 text-right font-medium text-purple-600">{month.six_day_week}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </AccordionSection>

      {/* Upload History */}
      <AccordionSection
        title="Upload History"
        icon={<History className="w-4 h-4 text-gray-600" />}
        iconBg="bg-gray-100"
        expanded={sectionsExpanded.history}
        onToggle={() => toggleSection('history')}
        theme="admin"
        contentVariant="padded"
        trailing={uploadHistory.length > 0 ? <InfoBadge color="gray" label={`${uploadHistory.length} uploads`} /> : null}
      >
        {uploadHistory.length === 0 ? (
          <div className="text-center py-8">
            <FileText className="w-12 h-12 mx-auto text-gray-400 mb-4" />
            <p className="text-gray-500">No uploads yet</p>
            <p className="text-sm text-gray-400 mt-1">Upload your first file to see the history here</p>
          </div>
        ) : (
          <div className="space-y-3">
            {uploadHistory.map((upload) => (
              <div key={upload.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors">
                <div className="flex items-center gap-3">
                  {getStatusIcon(upload.upload_status)}
                  <div>
                    <p className="font-medium text-sm text-gray-900">{upload.file_name}</p>
                    <p className="text-xs text-gray-500">
                      {new Date(upload.created_at).toLocaleDateString()} • 
                      {upload.year_processed && ` Year ${upload.year_processed} • `}
                      {upload.records_created} records
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                    upload.upload_status === 'completed' ? 'bg-green-100 text-green-700' :
                    upload.upload_status === 'failed' ? 'bg-red-100 text-red-700' :
                    'bg-yellow-100 text-yellow-700'
                  }`}>
                    {upload.upload_status.charAt(0).toUpperCase() + upload.upload_status.slice(1)}
                  </span>
                  {upload.processing_log && (
                    <button
                      onClick={() => downloadProcessingLog(upload.id)}
                      className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
                      title="Download processing log"
                    >
                      <Download className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </AccordionSection>
    </div>
  );
};

export default MOMWorkingDaysUpload; 